import { repeat } from "@@/utilities/repeat";
import { assertNever } from "../assert-never";
import {
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import { type EntityState } from "../entity-status";
import { type FeeId } from "../financials/fees/fee-id";
import { type OrderGroupId } from "../order-groups/order-group-id";
import { type Order } from "../orders/order";
import { type OrderId } from "../orders/order-id";
import {
    emptyOrderItemId,
    type OrderItemId_V1,
} from "../orders/order-items/order-item-id";
import { isOrderItemWithProduct } from "../orders/order-items/order-item-v2-helper";
import type { PayRequestPaymentId } from "../pay-requests/pay-request-payment-id";
import type { PaymentId } from "../payments/payment-id";
import type { PaymentProvider } from "../payments/payment-providers";
import type { Price } from "../prices/price";
import { sumPrices } from "../prices/sum-prices";
import { alfredProviderId } from "../providers/provider";
import type { ProviderId } from "../providers/provider-id";
import type { UUID } from "../uuid";
import { transactionIdFactory, type TransactionId } from "./transaction-id";
import {
    transactionItemFactory,
    type TransactionItem,
} from "./transaction-item";
import { type TransactionType } from "./transaction-type";

type Transaction = {
    readonly _id: TransactionId;
    readonly _type: "TRANSACTION";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;
    readonly providerId: ProviderId;
    readonly orderId: OrderId | undefined;
    readonly orderSlug: string | undefined;
    readonly orderGroupId: OrderGroupId | undefined;
    readonly feeId?: FeeId;
    readonly items: TransactionItem[];
    readonly paymentProvider: PaymentProvider;
    readonly paymentId: PaymentId | UUID | PayRequestPaymentId;
    /** @deprecated moving to use settlement note instead */
    readonly payoutId: string;
    readonly transactionAmount: number;
    readonly vatAmount: number;
    readonly transactionType: TransactionType;
    readonly destination?: string;
    readonly paymentFee?: number;
    readonly percentage?: number;

    readonly stripeAccount?: string;
};

type TransactionWithOrderData = Transaction & {
    orderData: Pick<
        Order,
        | "_id"
        | "providerId"
        | "orderGroupId"
        | "contactInfo"
        | "slug"
        | "customerId"
        | "orderGroupReference"
        | "orderItems"
    >;
};

type TransactionForOrderParams = {
    order: Order;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    paymentFee: number | undefined;
    stripeAccount?: string;
};

type TransactionForShippingFeeParams = {
    shippingFee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
    stripeAccount?: string;
};
type TransactionForServiceFeeParams = {
    serviceFee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
    stripeAccount?: string;
};

function transactionCaptureFactory(
    params: TransactionForOrderParams,
): Transaction;
function transactionCaptureFactory(
    params: TransactionForShippingFeeParams,
): Transaction;
function transactionCaptureFactory(
    params: TransactionForServiceFeeParams,
): Transaction;
function transactionCaptureFactory(
    params:
        | TransactionForOrderParams
        | TransactionForShippingFeeParams
        | TransactionForServiceFeeParams,
): Transaction {
    if ("order" in params) {
        return _captureTransactionForOrderFactory(params);
    }
    if ("serviceFee" in params) {
        return _captureTransactionForServiceFeeFactory(params);
    }
    if ("shippingFee" in params) {
        return _captureTransactionForShippingFeeFactory(params);
    }
    assertNever(params);
}

const _captureTransactionForOrderFactory = (params: {
    order: Order;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    paymentFee: number | undefined;
    stripeAccount?: string;
}): Transaction => {
    if (params.payment === "STRIPE" && params.stripeAccount === undefined) {
        throw new Error("Missing stripeAccount for STRIPE payment");
    }

    const { order, payment, paymentId, paymentFee } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?

    const items = order.orderItems
        .filter(order => order.quantity.value > 0)
        .flatMap(orderItem => {
            // COMMENTED OUT CODE SINCE ORDER ITEMS
            // CAN ONLY BE V2 NOWADAYS
            // if (isOrderItem_V2(orderItem)) {
            const parent = order.orderItems.find(
                item =>
                    item._id === orderItem.parentId &&
                    isOrderItemWithProduct(item),
            );
            const parentProductId = parent
                ? isOrderItemWithProduct(parent)
                    ? parent.productId
                    : undefined
                : undefined;

            const item = transactionItemFactory({
                orderItemId: orderItem._id,
                title: orderItem.meta.title,
                quantity: orderItem.quantity.value,
                pricePerItem: orderItem.pricePerItem,
                productId: isOrderItemWithProduct(orderItem)
                    ? orderItem.productId
                    : undefined,
                parentItemId: orderItem.parentId,
                parentProductId: parentProductId,
            });
            return item;
        });

    const extraFee =
        order.extraOrderFee && order.extraOrderFee.amountIncludingVat > 0
            ? transactionItemFactory({
                  orderItemId: "" as OrderItemId_V1,
                  title: "Minimum order fee",
                  quantity: 1,
                  pricePerItem: order.extraOrderFee,
                  parentItemId: undefined,
              })
            : null;
    if (extraFee) items.push(extraFee);
    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: order.providerId,
        orderId: order._id,
        orderSlug: order.slug,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: order.totalPrice.amountIncludingVat,
        vatAmount: order.totalPrice.vatAmount,
        payoutId: "",
        transactionType: "CAPTURE",
        destination: order.destination,
        paymentFee: paymentFee,
        orderGroupId: order.orderGroupId,
        stripeAccount: params.stripeAccount,
    };
};

const _captureTransactionForShippingFeeFactory = (params: {
    shippingFee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
}): Transaction => {
    const { shippingFee, payment, paymentId, destination } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = [
        transactionItemFactory({
            title: "ShippingFee",
            quantity: 1,
            pricePerItem: shippingFee,
            orderItemId: emptyOrderItemId,
            parentItemId: undefined,
        }),
    ];

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: alfredProviderId,
        orderId: undefined,
        orderSlug: undefined,
        orderGroupId: undefined,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: shippingFee.amountIncludingVat,
        vatAmount: shippingFee.vatAmount,
        payoutId: "",
        transactionType: "CAPTURE",
        destination,
    };
};

const _captureTransactionForServiceFeeFactory = (params: {
    serviceFee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
}): Transaction => {
    const { serviceFee, payment, paymentId, destination } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = [
        transactionItemFactory({
            title: "Servicefee",
            quantity: 1,
            pricePerItem: serviceFee,
            orderItemId: emptyOrderItemId,
            parentItemId: undefined,
        }),
    ];

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: alfredProviderId,
        orderId: undefined,
        orderSlug: undefined,
        orderGroupId: undefined,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: serviceFee.amountIncludingVat,
        vatAmount: serviceFee.vatAmount,
        payoutId: "",
        transactionType: "CAPTURE",
        destination: destination,
    };
};

function createRefundTransactionFromOrder(
    order: Order,
    payment: PaymentProvider,
    paymentId: PaymentId,
    atDate?: IsoAndUnixTimestamp,
    percentage?: number,
    amount?: Price,
    account?: "STANDARD" | "ACCOMMODATION",
): Transaction;
function createRefundTransactionFromOrder(
    order: Order,
    payment: PaymentProvider,
    paymentId: UUID,
    atDate?: IsoAndUnixTimestamp,
    percentage?: number,
    amount?: Price,

    account?: "STANDARD" | "ACCOMMODATION",
): Transaction;
function createRefundTransactionFromOrder(
    order: Order,
    payment: PaymentProvider,
    paymentId: PaymentId | UUID,
    atDate?: IsoAndUnixTimestamp,
    percentage?: number,
    amount?: Price,

    account?: "STANDARD" | "ACCOMMODATION",
): Transaction;
function createRefundTransactionFromOrder(
    order: Order,
    payment: PaymentProvider,
    paymentId: PaymentId | UUID,
    atDate?: IsoAndUnixTimestamp,
    percentage?: number,
    amount?: Price,

    account?: "STANDARD" | "ACCOMMODATION",
): Transaction {
    const _createdAt = atDate ?? nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = order.orderItems
        .filter(o => o.quantity.value > 0)
        .flatMap(orderItem => {
            const item = transactionItemFactory({
                orderItemId: orderItem._id,
                title: orderItem.meta.title,
                quantity: orderItem.quantity.value,
                pricePerItem: orderItem.pricePerItem,
                productId: isOrderItemWithProduct(orderItem)
                    ? orderItem.productId
                    : undefined,
                parentItemId: orderItem.parentId,
            });
            return item;
        });
    const extraFee =
        order.extraOrderFee && order.extraOrderFee.amountIncludingVat > 0
            ? transactionItemFactory({
                  orderItemId: emptyOrderItemId,
                  title: "Minimum order fee",
                  quantity: 1,
                  pricePerItem: order.extraOrderFee,
                  parentItemId: undefined,
              })
            : null;
    if (extraFee) items.push(extraFee);

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt,
        providerId: order.providerId,
        orderId: order._id,
        orderSlug: order.slug,
        orderGroupId: order.orderGroupId,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: amount
            ? -amount.amountIncludingVat
            : -order.totalPrice.amountIncludingVat * (percentage ?? 1),

        vatAmount: amount
            ? -amount.vatAmount
            : -order.totalPrice.vatAmount * (percentage ?? 1),
        payoutId: "",
        transactionType: "REFUND",
        destination: order.destination,
        percentage: percentage,
        stripeAccount: account,
    };
}

const createRefundTransactionFromShippingFee = (params: {
    fee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
}): Transaction => {
    const { fee, payment, paymentId, destination } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = [
        transactionItemFactory({
            title: "ShippingFee",
            quantity: 1,
            pricePerItem: fee,
            orderItemId: emptyOrderItemId,
            parentItemId: undefined,
        }),
    ];

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: alfredProviderId,
        orderId: undefined,
        orderSlug: undefined,
        orderGroupId: undefined,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: -fee.amountIncludingVat,
        vatAmount: -fee.vatAmount,
        payoutId: "",
        transactionType: "REFUND",
        destination,
    };
};

const createRefundTransaction = (params: {
    amount: Price;
    order: Order;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    percentage?: number;
    stripeAccount?: "STANDARD" | "ACCOMMODATION";
}): Transaction => {
    const { amount, order, payment, paymentId } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = [
        transactionItemFactory({
            title: "Refund",
            quantity: 1,
            pricePerItem: amount,
            orderItemId: emptyOrderItemId,
            parentItemId: undefined,
        }),
    ];

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: order.providerId,
        orderId: order._id,
        orderSlug: order.slug,
        orderGroupId: order.orderGroupId,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount:
            -amount.amountIncludingVat * (params.percentage ?? 1),
        vatAmount: -amount.vatAmount * (params.percentage ?? 1),
        payoutId: "",
        transactionType: "REFUND",
        percentage: params.percentage,
        stripeAccount: params.stripeAccount,
    };
};

const createRefundTransactionFromServiceFee = (params: {
    serviceFee: Price;
    payment: PaymentProvider;
    paymentId: PaymentId | UUID;
    destination?: string;
}): Transaction => {
    const { serviceFee, payment, paymentId, destination } = params;
    const justNow = nowIsoAndUnix();
    //Should probably check if a item has been removed-status?
    const items = [
        transactionItemFactory({
            title: "Servicefee",
            quantity: 1,
            pricePerItem: serviceFee,
            orderItemId: emptyOrderItemId,
            parentItemId: undefined,
        }),
    ];

    return {
        _id: transactionIdFactory(),
        _type: "TRANSACTION",
        _status: "ACTIVE",
        _createdAt: justNow,
        providerId: alfredProviderId,
        orderId: undefined,
        orderSlug: undefined,
        orderGroupId: undefined,
        items: items,
        paymentProvider: payment,
        paymentId: paymentId,
        transactionAmount: -serviceFee.amountIncludingVat,
        vatAmount: -serviceFee.vatAmount,
        payoutId: "",
        transactionType: "REFUND",
        destination,
    };
};

//Check to see if transaction is a refund of an order change
//General refund have a positive-rows and a negative header (transactionAmount)
//Order change refunds will have both positive and negative rows (ADD,REMOVED)
//and the header will be negative as well
const isOrderChangeRefund = (transaction: Transaction) => {
    if (transaction.transactionType !== "REFUND") return false;
    const sum = sumPrices(
        transaction.items.flatMap(item =>
            repeat(item.pricePerItem, item.quantity),
        ),
    );
    const orderChanged =
        transaction.transactionType === "REFUND" &&
        sum.amountIncludingVat === transaction.transactionAmount;

    return orderChanged;
};

export {
    createRefundTransaction,
    createRefundTransactionFromOrder,
    createRefundTransactionFromServiceFee,
    createRefundTransactionFromShippingFee,
    isOrderChangeRefund,
    transactionCaptureFactory,
};
export type { Transaction, TransactionWithOrderData };
